import React, { useCallback, useRef, useState } from 'react';
import { Box } from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import { HALL_HEIGHT_M, HALL_WIDTH_M, PlacedSignal, SHOPFLOOR_WIDGET_DND } from './types';
import { SignalSlot } from '../signals/types';
import { ResolvedSlotStatus } from '../signals/resolveLiveStatus';
import { WidgetCatalogItem } from '../types';
import hallFloorUrl from './assets/hall-floor.png';

/** Axis labels sit just outside the hall; pad so the full plan fits at max scale. */
const VIEW_PAD_L = 2.8;
const VIEW_PAD_T = 2.2;
const VIEW_PAD_R = 0.8;
const VIEW_PAD_B = 1.2;
const FIT_VIEWBOX = `${-VIEW_PAD_L} ${-VIEW_PAD_T} ${HALL_WIDTH_M + VIEW_PAD_L + VIEW_PAD_R} ${HALL_HEIGHT_M + VIEW_PAD_T + VIEW_PAD_B}`;

export interface HallSignalVisual extends PlacedSignal {
  signals: SignalSlot[];
  liveResolved?: ResolvedSlotStatus[];
  temp?: number | null;
  rh?: number | null;
  energy?: number | null;
  power?: number | null;
  switchOn?: boolean | null;
}

interface HallBoardProps {
  signals: HallSignalVisual[];
  selectedSignalId: string | null;
  editMode: boolean;
  onSelectSignal: (item: PlacedSignal) => void;
  onActivateSignal?: (item: PlacedSignal) => void;
  onMoveSignal: (id: string, x: number, y: number) => void;
  onDropWidget?: (item: WidgetCatalogItem, x: number, y: number) => void;
}

const COLUMNS = [5.7, 13.7, 21.7, 29.7, 37.7, 45.7, 53.7];

function segmentModeClass(mode: string | undefined): string {
  switch (mode) {
    case 'off':
      return 'sf-off';
    case 'blink':
      return 'sf-blink';
    case 'fast_blink':
      return 'sf-fast-blink';
    case 'flash':
      return 'sf-flash';
    default:
      return 'sf-steady';
  }
}

function formatHallQty(n: number | null | undefined, digits: number, suffix: string): string {
  if (n == null || !Number.isFinite(n)) {
    return '—';
  }
  return `${n.toFixed(digits)}${suffix}`;
}

function ClimateMarker({
  item,
  selected,
  editMode,
  onPointerDown,
}: {
  item: HallSignalVisual;
  selected: boolean;
  editMode: boolean;
  onPointerDown: (item: HallSignalVisual, e: React.PointerEvent) => void;
}) {
  const theme = useTheme();
  const w = 2.55;
  const h = 1.42;
  const x = item.x - w / 2;
  const y = item.y - h;
  const paper = theme.palette.background.paper;
  const tempText = formatHallQty(item.temp, 1, '°');
  const rhText = formatHallQty(item.rh, 0, '%');
  return (
    <g
      className="placed-signal"
      style={{ cursor: editMode ? 'move' : 'pointer' }}
      onPointerDown={(e) => onPointerDown(item, e)}
    >
      {selected && (
        <rect
          x={x - 0.12}
          y={y - 0.12}
          width={w + 0.24}
          height={h + 0.55}
          rx={0.22}
          fill="none"
          stroke={theme.palette.primary.main}
          strokeWidth={0.1}
        />
      )}
      <rect
        x={x}
        y={y}
        width={w}
        height={h}
        rx={0.18}
        fill={paper}
        stroke={theme.palette.divider}
        strokeWidth={0.06}
      />
      <rect x={x + 0.32} y={y + 0.16} width={0.14} height={0.62} rx={0.07} fill="#c62828" />
      <circle cx={x + 0.39} cy={y + 0.86} r={0.2} fill="#c62828" />
      <text
        x={x + 0.72}
        y={y + 0.58}
        fill={item.temp != null ? theme.palette.text.primary : theme.palette.text.disabled}
        fontSize={0.42}
        fontWeight={700}
        style={{ pointerEvents: 'none' }}
      >
        {tempText}
      </text>
      <text
        x={x + 0.72}
        y={y + 1.2}
        fill={item.rh != null ? theme.palette.text.primary : theme.palette.text.disabled}
        fontSize={0.34}
        style={{ pointerEvents: 'none' }}
      >
        {rhText}
      </text>
      <text
        x={item.x}
        y={item.y + 0.42}
        textAnchor="middle"
        fill={theme.palette.text.secondary}
        fontSize={0.32}
        style={{ pointerEvents: 'none' }}
      >
        {item.label}
      </text>
    </g>
  );
}

function formatHallPower(w: number | null | undefined): string {
  if (w == null || !Number.isFinite(w)) {
    return '—';
  }
  if (Math.abs(w) >= 1000) {
    return `${(w / 1000).toFixed(1)}kW`;
  }
  return `${Math.round(w)}W`;
}

function formatHallEnergy(kwh: number | null | undefined): string {
  if (kwh == null || !Number.isFinite(kwh)) {
    return '—';
  }
  if (Math.abs(kwh) >= 1000) {
    return `${(kwh / 1000).toFixed(1)} MWh`;
  }
  return `${kwh.toFixed(1)} kWh`;
}

function SmlMeterMarker({
  item,
  selected,
  editMode,
  onPointerDown,
}: {
  item: HallSignalVisual;
  selected: boolean;
  editMode: boolean;
  onPointerDown: (item: HallSignalVisual, e: React.PointerEvent) => void;
}) {
  const theme = useTheme();
  const w = 2.7;
  const h = 1.42;
  const x = item.x - w / 2;
  const y = item.y - h;
  const paper = theme.palette.background.paper;
  const powerText = formatHallPower(item.power);
  const energyText = formatHallEnergy(item.energy);
  return (
    <g
      className="placed-signal"
      style={{ cursor: editMode ? 'move' : 'pointer' }}
      onPointerDown={(e) => onPointerDown(item, e)}
    >
      {selected && (
        <rect
          x={x - 0.12}
          y={y - 0.12}
          width={w + 0.24}
          height={h + 0.55}
          rx={0.22}
          fill="none"
          stroke={theme.palette.primary.main}
          strokeWidth={0.1}
        />
      )}
      <rect
        x={x}
        y={y}
        width={w}
        height={h}
        rx={0.18}
        fill={paper}
        stroke={theme.palette.divider}
        strokeWidth={0.06}
      />
      <path
        d={`M${x + 0.45} ${y + 0.35} L${x + 0.7} ${y + 0.35} L${x + 0.55} ${y + 0.72} L${x + 0.78} ${y + 0.72} L${x + 0.42} ${y + 1.18} L${x + 0.52} ${y + 0.82} L${x + 0.32} ${y + 0.82} Z`}
        fill={item.power != null ? '#f9a825' : theme.palette.action.disabled}
      />
      <text
        x={x + 0.95}
        y={y + 0.58}
        fill={item.power != null ? theme.palette.text.primary : theme.palette.text.disabled}
        fontSize={0.4}
        fontWeight={700}
        style={{ pointerEvents: 'none' }}
      >
        {powerText}
      </text>
      <text
        x={x + 0.95}
        y={y + 1.2}
        fill={item.energy != null ? theme.palette.text.primary : theme.palette.text.disabled}
        fontSize={0.32}
        style={{ pointerEvents: 'none' }}
      >
        {energyText}
      </text>
      <text
        x={item.x}
        y={item.y + 0.42}
        textAnchor="middle"
        fill={theme.palette.text.secondary}
        fontSize={0.32}
        style={{ pointerEvents: 'none' }}
      >
        {item.label}
      </text>
    </g>
  );
}

function SwitchMarker({
  item,
  selected,
  editMode,
  onPointerDown,
}: {
  item: HallSignalVisual;
  selected: boolean;
  editMode: boolean;
  onPointerDown: (item: HallSignalVisual, e: React.PointerEvent) => void;
}) {
  const theme = useTheme();
  const on = item.switchOn === true;
  const bulb = item.icon === 'bulb';
  const r = 0.55;
  return (
    <g
      className="placed-signal"
      style={{ cursor: editMode ? 'move' : (bulb ? 'default' : 'pointer') }}
      onPointerDown={(e) => onPointerDown(item, e)}
    >
      {selected && (
        <circle
          cx={item.x}
          cy={item.y - r}
          r={r + 0.22}
          fill="none"
          stroke={theme.palette.primary.main}
          strokeWidth={0.1}
        />
      )}
      <circle
        cx={item.x}
        cy={item.y - r}
        r={r}
        fill={on ? (bulb ? '#f9a825' : theme.palette.success.main) : theme.palette.action.disabled}
        stroke={theme.palette.divider}
        strokeWidth={0.06}
        opacity={on ? 1 : 0.55}
      />
      {bulb ? (
        <path
          d={`M${item.x - 0.18} ${item.y - r - 0.12}
             Q${item.x} ${item.y - r - 0.42} ${item.x + 0.18} ${item.y - r - 0.12}
             L${item.x + 0.12} ${item.y - r + 0.18}
             L${item.x - 0.12} ${item.y - r + 0.18} Z`}
          fill={on ? '#fff8e1' : theme.palette.background.paper}
          style={{ pointerEvents: 'none' }}
        />
      ) : (
        <rect
          x={item.x - 0.28}
          y={item.y - r - 0.12}
          width={0.56}
          height={0.24}
          rx={0.12}
          fill={theme.palette.background.paper}
          style={{ pointerEvents: 'none' }}
        />
      )}
      <text
        x={item.x}
        y={item.y + 0.42}
        textAnchor="middle"
        fill={theme.palette.text.secondary}
        fontSize={0.32}
        style={{ pointerEvents: 'none' }}
      >
        {item.label}
      </text>
    </g>
  );
}

function StacklightMarker({
  item,
  selected,
  editMode,
  onPointerDown,
}: {
  item: HallSignalVisual;
  selected: boolean;
  editMode: boolean;
  onPointerDown: (item: HallSignalVisual, e: React.PointerEvent) => void;
}) {
  const theme = useTheme();
  const segs = [...(item.signals || [])].reverse();
  const segH = 0.45;
  const segW = 0.9;
  const gap = 0.08;
  const totalH = Math.max(segs.length, 1) * (segH + gap) + 0.35;
  return (
    <g
      className="placed-signal"
      style={{ cursor: editMode ? 'move' : 'pointer' }}
      onPointerDown={(e) => onPointerDown(item, e)}
    >
      {selected && (
        <rect
          x={item.x - segW / 2 - 0.2}
          y={item.y - totalH - 0.15}
          width={segW + 0.4}
          height={totalH + 0.45}
          rx={0.15}
          fill="none"
          stroke={theme.palette.primary.main}
          strokeWidth={0.1}
        />
      )}
      <rect
        x={item.x - segW / 2}
        y={item.y - totalH}
        width={segW}
        height={0.2}
        rx={0.08}
        fill="#2a2f36"
      />
      {(segs.length ? segs : [{ id: 'empty', name: '', statuses: [{ id: 'off', name: 'Off', color: '#000000', mode: 'off' as const }] }]).map((slot, i) => {
        const live = item.liveResolved
          ? [...item.liveResolved].reverse()[i]
          : undefined;
        const st = live || ('statuses' in slot
          ? (slot.statuses.find((s) => s.id === (('previewStatus' in slot ? slot.previewStatus : undefined) || slot.statuses[0]?.id)) || slot.statuses[0])
          : undefined);
        const color = live?.color || (st && 'color' in st ? st.color : '#000000');
        const mode = live?.mode || (st && 'mode' in st ? st.mode : 'steady');
        const y = item.y - totalH + 0.25 + i * (segH + gap);
        return (
          <rect
            key={slot.id}
            className={segmentModeClass(mode)}
            x={item.x - segW / 2}
            y={y}
            width={segW}
            height={segH}
            rx={0.08}
            fill={color}
          />
        );
      })}
      <rect
        x={item.x - segW / 2}
        y={item.y - 0.12}
        width={segW}
        height={0.2}
        rx={0.08}
        fill="#2a2f36"
      />
      <text
        x={item.x}
        y={item.y + 0.45}
        textAnchor="middle"
        fill={theme.palette.text.secondary}
        fontSize={0.35}
        style={{ pointerEvents: 'none' }}
      >
        {item.label || item.signalName}
      </text>
    </g>
  );
}

const HallBoard: React.FC<HallBoardProps> = ({
  signals,
  selectedSignalId,
  editMode,
  onSelectSignal,
  onActivateSignal,
  onMoveSignal,
  onDropWidget,
}) => {
  const theme = useTheme();
  const svgRef = useRef<SVGSVGElement>(null);
  const [dropActive, setDropActive] = useState(false);

  const clientToSvg = useCallback((cx: number, cy: number) => {
    const svg = svgRef.current;
    if (!svg) return { x: 0, y: 0 };
    const pt = svg.createSVGPoint();
    pt.x = cx;
    pt.y = cy;
    const ctm = svg.getScreenCTM();
    if (!ctm) return { x: 0, y: 0 };
    const p = pt.matrixTransform(ctm.inverse());
    return { x: p.x, y: p.y };
  }, []);

  const onSignalPointerDown = (item: HallSignalVisual, e: React.PointerEvent) => {
    e.stopPropagation();
    e.preventDefault();
    onSelectSignal(item);
    const startClient = { x: e.clientX, y: e.clientY };
    const start = clientToSvg(e.clientX, e.clientY);
    const ox = item.x;
    const oy = item.y;
    let dragged = false;

    const move = (ev: PointerEvent) => {
      const dist = Math.hypot(ev.clientX - startClient.x, ev.clientY - startClient.y);
      if (dist > 4) dragged = true;
      if (!editMode) return;
      const p = clientToSvg(ev.clientX, ev.clientY);
      const nx = Math.max(0.5, Math.min(HALL_WIDTH_M - 0.5, ox + p.x - start.x));
      const ny = Math.max(0.5, Math.min(HALL_HEIGHT_M - 0.5, oy + p.y - start.y));
      onMoveSignal(item.id, nx, ny);
    };
    const up = () => {
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', up);
      if (!dragged && (item.type === 'stacklight' || item.type === 'switch')) {
        onActivateSignal?.(item);
      }
    };
    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', up);
  };

  const gridStroke = theme.palette.mode === 'dark' ? alpha('#fff', 0.06) : alpha('#1b1f24', 0.08);
  const majorStroke = theme.palette.mode === 'dark' ? alpha('#fff', 0.1) : alpha('#1b1f24', 0.12);
  const wallStroke = theme.palette.text.primary;
  const paper = theme.palette.background.paper;

  return (
    <Box
      sx={{
        position: 'relative',
        height: '100%',
        width: '100%',
        minHeight: 0,
        bgcolor: theme.palette.mode === 'dark' ? 'grey.900' : 'grey.100',
        overflow: 'hidden',
        cursor: 'default',
        outline: dropActive ? `2px dashed ${theme.palette.primary.main}` : 'none',
        outlineOffset: -4,
      }}
      onDragOver={(e) => {
        if (!editMode) return;
        e.preventDefault();
        e.dataTransfer.dropEffect = 'copy';
        setDropActive(true);
      }}
      onDragLeave={() => setDropActive(false)}
      onDrop={(e) => {
        if (!editMode || !onDropWidget) return;
        e.preventDefault();
        setDropActive(false);
        const raw = e.dataTransfer.getData(SHOPFLOOR_WIDGET_DND);
        if (!raw) return;
        let item: WidgetCatalogItem;
        try {
          item = JSON.parse(raw) as WidgetCatalogItem;
        } catch {
          return;
        }
        const p = clientToSvg(e.clientX, e.clientY);
        onDropWidget(item, p.x, p.y);
      }}
    >
      <Box
        component="svg"
        ref={svgRef}
        viewBox={FIT_VIEWBOX}
        preserveAspectRatio="xMidYMid meet"
        xmlns="http://www.w3.org/2000/svg"
        sx={{ width: '100%', height: '100%', display: 'block', userSelect: 'none' }}
      >
        <defs>
          <pattern id="shopfloorMinor" width="1" height="1" patternUnits="userSpaceOnUse">
            <path d="M1 0L0 0 0 1" fill="none" stroke={gridStroke} strokeWidth={0.025} />
          </pattern>
          <pattern id="shopfloorMajor" width="5" height="5" patternUnits="userSpaceOnUse">
            <rect width="5" height="5" fill="url(#shopfloorMinor)" />
            <path d="M5 0L0 0 0 5" fill="none" stroke={majorStroke} strokeWidth={0.045} />
          </pattern>
          <style>{`
            @keyframes sfBlink { 0%, 49% { opacity: 1; } 50%, 100% { opacity: 0.18; } }
            @keyframes sfFastBlink { 0%, 44% { opacity: 1; } 45%, 100% { opacity: 0.12; } }
            @keyframes sfFlash { 0%, 12% { opacity: 1; } 13%, 100% { opacity: 0.12; } }
            .sf-off { opacity: 0.2; }
            .sf-steady { opacity: 0.95; }
            .sf-blink { animation: sfBlink 1s step-end infinite; }
            .sf-fast-blink { animation: sfFastBlink 0.35s step-end infinite; }
            .sf-flash { animation: sfFlash 1.1s step-end infinite; }
          `}</style>
        </defs>

        <rect x={-4} y={-4} width={68} height={40} fill={paper} />
        {React.createElement('image', {
          href: hallFloorUrl,
          xlinkHref: hallFloorUrl,
          x: 0,
          y: 0,
          width: HALL_WIDTH_M,
          height: HALL_HEIGHT_M,
          preserveAspectRatio: 'none',
          style: { pointerEvents: 'none' },
        })}
        <rect
          x={0}
          y={0}
          width={HALL_WIDTH_M}
          height={HALL_HEIGHT_M}
          fill="url(#shopfloorMajor)"
          opacity={0.28}
          style={{ pointerEvents: 'none' }}
        />

        {Array.from({ length: 13 }, (_, i) => i * 5).map((x) => (
          <text
            key={`rx-${x}`}
            x={x}
            y={-1.55}
            textAnchor={x === 0 ? 'start' : x === 60 ? 'end' : 'middle'}
            fill={theme.palette.text.secondary}
            fontSize={0.55}
          >
            {x} m
          </text>
        ))}
        {Array.from({ length: 7 }, (_, i) => i * 5).map((y) => (
          <text
            key={`ry-${y}`}
            x={-1.55}
            y={y + 0.18}
            textAnchor="end"
            fill={theme.palette.text.secondary}
            fontSize={0.55}
          >
            {y} m
          </text>
        ))}

        <rect x={0} y={0} width={HALL_WIDTH_M} height={HALL_HEIGHT_M} fill="none" stroke={wallStroke} strokeWidth={0.18} />
        <path d="M0 27 H38 V22 H46 V32" fill="none" stroke={theme.palette.text.secondary} strokeWidth={0.09} />
        <path d="M38 22 V32 M43 22 V32 M46 22 H60" fill="none" stroke={theme.palette.text.secondary} strokeWidth={0.09} />
        <path d="M51 22 V32 M56 22 V32" fill="none" stroke={theme.palette.text.secondary} strokeWidth={0.09} />

        {COLUMNS.map((x) => (
          <rect
            key={x}
            x={x}
            y={18.8}
            width={0.6}
            height={0.6}
            fill={paper}
            stroke={theme.palette.text.secondary}
            strokeWidth={0.1}
          />
        ))}

        {signals.map((item) => {
          if (item.type === 'temperature') {
            return (
              <ClimateMarker
                key={item.id}
                item={item}
                selected={item.id === selectedSignalId}
                editMode={editMode}
                onPointerDown={onSignalPointerDown}
              />
            );
          }
          if (item.type === 'sml_meter') {
            return (
              <SmlMeterMarker
                key={item.id}
                item={item}
                selected={item.id === selectedSignalId}
                editMode={editMode}
                onPointerDown={onSignalPointerDown}
              />
            );
          }
          if (item.type === 'switch') {
            return (
              <SwitchMarker
                key={item.id}
                item={item}
                selected={item.id === selectedSignalId}
                editMode={editMode}
                onPointerDown={onSignalPointerDown}
              />
            );
          }
          return (
            <StacklightMarker
              key={item.id}
              item={item}
              selected={item.id === selectedSignalId}
              editMode={editMode}
              onPointerDown={onSignalPointerDown}
            />
          );
        })}
      </Box>
    </Box>
  );
};

export default HallBoard;
